<template>
	<chatbot
		:alerts="alerts"
		:translations="translations"
		:code="code"
		:prevent-unload="preventUnload"
		:show-chatbot="showChatbot"
		:title="title"
		@close="closeChat"
	/>
</template>

<script>
	import Message from "./chatbotInstructions/Message";
	import Call from "./chatbotInstructions/Call";
	import Select from "./chatbotInstructions/Select";
	import SelectOption from "./chatbotInstructions/SelectOption";
	import Form from "./chatbotInstructions/Form";
	import FormField from "./chatbotInstructions/FormField";
	import Custom from "./chatbotInstructions/Custom";
	import Restart from "./chatbotInstructions/Restart";
	import Flashes from "./chatbotInstructions/Flashes";

	export default {
		props: {
			translations: {
				type: Object,
				required: true,
			},
			urls: {
				type: Object,
				required: true,
			},
			alerts: {
				type: Object,
				required: true,
			},
			showChatbot: {
				type: Boolean,
				required: true,
			},
			title: {
				type: String,
				default: ''
			},
		},
		data ()
		{
			const trans = this.translations.fileImport;
			const self = this;

			return {
				preventUnload: false,
				code: {
					main: [
						new Call('download_or_upload'),
					],

					download_or_upload: [
						new Message(trans.download_or_upload.intro),
						new Select(
							'nextCall',
							[
								new SelectOption('download', trans.download_or_upload.options.download.label).hasHref(self.urls.template),
								new SelectOption('upload', trans.download_or_upload.options.upload.label),
							]
						),
						new Message((variables) => trans.download_or_upload.options[variables.nextCall].response)
							.isFromUser(),
						new Call((variables) => variables.nextCall),
					],

					download: [
						new Message(trans.download.intro),
						new Select(
							null,
							[new SelectOption(null, trans.download.button)]
						),
						new Message(trans.download.response).isFromUser(),
						new Call('upload'),
					],

					upload: [
						new Message(trans.upload.intro),
						new Message(trans.upload.warning),
						new Call(
							function()
							{
								self.preventUnload = true;
								clearError();
							}
						),
						new Form(
							trans.upload.submit,
							[
								new FormField('file', trans.upload.field).isFile().isRequired(),
							]

						).validateWith(
							function(variables)
							{
								let formData = new FormData();
								formData.append('file', variables.file[0]);
								variables.checkStatus = false;
								variables.reference = null;

								return new ApiMonizze()
									.postDataFromApi(
										self.urls.send,
										formData,
										function(data)
										{
											variables.reference = data.reference;
											variables.checkStatus = true;
										}
									);
							}
						),
						new Message(trans.upload.response).isFromUser(),
						new Call('check'),
					],
					check: [
						new Message(trans.check.intro),
						new Message(trans.check.time),
						new Custom(
							async function(variables)
							{
								let donePooling = false;
								variables.nothing_to_do = false;
								variables.messages = {
									renew: null,
									contract_change: null,
									warnings: null
								}

								while(!donePooling)
								{
									await new ApiMonizze()
										.getDataFromApi(
											self.urls.stats.replace('{reference}', variables.reference),
											function(data)
											{
												if (data === null || data.total === undefined)
												{
													return;
												}

												if (data.total > 0)
												{
													variables.messages.intro = trans.summary.intro
														.replace('{total}', data.total)
														.replace('{s}', data.total === 1 ? '' : 's')
														.replace('{en}', data.total === 1 ? '' : 'en');

													variables.messages.renew = trans.summary.renewals
														.replace('{amount}', data.todo.renew_all)
														.replace('{s}', data.todo.renew_all === 1 ? '' : 's')
														.replace('{en}', data.todo.renew_all === 1 ? '' : 'en');
												}
												else
												{
													variables.nothing_to_do = true;
												}

												if (data.invalid !== undefined && data.invalid.length > 0)
												{
													variables.messages.invalids = trans.summary.invalids
														+ '<ul><li>'
														+ data.invalid
															.map(
																record =>
																	record.data.fullname
																	+ ' : '
																	+ trans.summary.errors[record.reason]
															)
												  			.map(htmlEncode)
															.join('</li><li>')
														+ '</li></ul>';
												}
												donePooling = true;
											}
										);

									if (!donePooling)
									{
										// We wait 1sec between retry
										await new Promise(resolve => setTimeout(resolve, 1000));
									}
								}
							}
						),
						new Call('summary').if((variables) => !variables.nothing_to_do),
						new Call('nothing_to_do').if((variables) => variables.nothing_to_do),
					],
					summary: [
						new Message((variables) => variables.messages.intro),
						new Message((variables) => variables.messages.renew).if((variables) => variables.messages.renew),
						new Message((variables) => variables.messages.invalids, true).if((variables) => variables.messages.invalids),
						new Custom(
							function (variables)
							{
								// reset messages
								variables.messages = {
									renew: null,
									contract_change: null,
									warnings: null
								};
							}
						),
						new Select(
							'action',
							Object.entries(trans.summary.options)
								.map(([key, value]) => new SelectOption(key, value.label))
						),
						new Message((variables) => trans.summary.options[variables.action].response)
							.isFromUser(),
						new Call('confirmed').if((variables) => variables.action === 'correct'),
						new Custom(
							async function(variables)
							{
								new ApiMonizze()
									.postDataFromApi(
										self.urls.cancel.replace('{reference}', variables.reference),
										{},
										function() {}
									)
									.catch(() => {});
							}
						).if((variables) => variables.action === 'retry' || variables.action === 'cancel'),
						new Restart('upload').if((variables) => variables.action === 'retry'),
						new Custom(
							function()
							{
								self.$emit('cancel');
							}
						).if((variables) => variables.action === 'cancel'),
					],
					confirmed: [
						new Message(trans.confirmed.intro),
						new Call(
							function ()
							{
								self.preventUnload = false;
								clearError();
							}
						),
						new Custom(
							async function(variables)
							{
								await new ApiMonizze()
									.postDataFromApi(
										self.urls.confirm.replace('{reference}', variables.reference),
										{},
										function() {}
									);

								let donePooling = false;

								while(!donePooling)
								{
									await new ApiMonizze()
										.getDataFromApi(
											self.urls.status.replace('{reference}', variables.reference),
											function(data)
											{
												if (data.warnings && data.warnings.length > 0)
												{
													variables.warnings = data.warnings.map(htmlEncode).join('<br />');
													donePooling = true;
													return;
												}

												if (!data.ready)
												{
													return;
												}
												donePooling = true;
											}
										);

									if (!donePooling)
									{
										await new Promise(resolve => setTimeout(resolve, 1000));
									}
								}

								// Clear reference
								variables.reference = null;
								if (donePooling && (variables.warnings === undefined || variables.warnings.length <= 0))
								{
									self.$emit('processed', variables.reference);
								}
							}
						),
						new Call('error').if((variables) => variables.warnings),
					],
					nothing_to_do: [
						new Message(trans.nothing_to_do.intro),
						new Select(
							'action',
							Object.entries(trans.nothing_to_do.options)
								.map(([key, value]) => new SelectOption(key, value.label))
						),
						new Message((variables) => trans.nothing_to_do.options[variables.action].response)
							.isFromUser(),
						new Custom(
							async function(variables)
							{
								new ApiMonizze()
									.postDataFromApi(
										self.urls.cancel.replace('{reference}', variables.reference),
										{},
										function() {}
									)
									.catch(() => {});
							}
						),
						new Restart('upload').if((variables) => variables.action === 'retry'),
						new Custom(
							function()
							{
								self.$emit('cancel');
							}
						).if((variables) => variables.action === 'cancel'),
					],
					error: [
						new Flashes((variables) => variables.errors),
						new Message((variables) => variables.warnings, true).if((variables) => variables.warnings),
						new Message(trans.error.intro),
						new Select(
							'nextCall',
							Object.entries(trans.error.options)
								.map(([key, value]) => new SelectOption(key, value.label))
						),
						new Message((variables) => trans.error.options[variables.nextCall].response)
							.isFromUser(),
						new Call((variables) => variables.nextCall),
					],
					help: [
					 	new Message(trans.help.intro),
					],
				},
			}
		},
		methods: {
			closeChat()
			{
				this.$emit('close');
			}
		}
	}
</script>
